/**
 * What the drawer reports when something offered alongside a product is picked.
 *
 * Legacy had one event pair for this, `option_selected` / `option_removed`, and
 * v2 keeps it — the drawer's four lists are one act as far as the funnel is
 * concerned, and splitting them into four series would make the v2 numbers
 * incomparable with the legacy history for no gain. What each list was rides on
 * the v2 payload instead.
 *
 * Kept out of the component because "was this picked?" is not obvious for a
 * quantified option: the same callback carries a tick, a count and an enum
 * choice, and only some of their values mean the client added something.
 */

import type { CatalogueEntryRole } from 'utils/purchaseFlowRevamp/catalogueEntryRole'
import type { AddonToggledPayload } from '../../analytics/events'

/**
 * The frontend's role vocabulary translated to the wire's, which is what the
 * backend and Salesforce both call it (`Offer_In_Catalogue__c`). The analytics
 * payload deliberately speaks the wire's language: a chart outlives whichever
 * shorthand the UI layer happens to use for its two lists.
 */
export const addonRoleForAnalytics = (role: CatalogueEntryRole): AddonToggledPayload['role'] =>
  role === 'standalone' ? 'standalone' : 'main_product_option'

/**
 * Whether an option's new value means the client HAS the add-on.
 *
 * One callback carries all three option kinds: a checkbox sends a boolean, a
 * bounded number sends a count, and an enum sends the chosen value. A count of
 * zero and an empty enum choice are removals wearing a set-value call, which is
 * the case a naive truthiness check on the whole union gets right only by
 * accident and a `!== undefined` check gets wrong outright.
 */
export const isAddonSelectedValue = (value: boolean | number | string | undefined): boolean => {
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value > 0
  if (typeof value === 'string') return value.length > 0
  return false
}

/**
 * What a plain toggle will leave behind, computed from the selection BEFORE the
 * click is applied.
 *
 * `toggleExtra` takes an id and nothing else, so the direction has to be derived
 * from the current selection. Deriving it after the store has moved reports the
 * state the click produced as the state it started from — every selection
 * arrives as a removal and vice versa, which is invisible in aggregate because
 * the two series simply swap.
 */
export const nextExtraSelection = (
  selectedExtraIds: readonly string[],
  extraId: string
): boolean => !selectedExtraIds.includes(extraId)
